import {Map} from 'immutable';
import {css} from 'styled-components';
import i18next from 'i18next';

import {type RootState} from 'web-app/react/redux/root-state';
import {getSpacing, s2, s4, s6} from 'web-app/styleguide/spacing';
import * as Breakpoints from 'web-app/styleguide/breakpoint-helpers';
import {type GenericFormData, GenericFieldType, type GenericFieldElement} from 'web-app/react/components/form/model';
import * as ChildPickupModalHelpers from 'web-app/react/modals-container/child-pickup-modal/helpers';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {isNonEmpty} from 'web-app/util/non-empty-array';
import {required} from 'web-app/react/components/form-elements/validators';

import {childCheckinFromAPI, type Statement} from './models';
import {
    QR_SIGNIN_MODAL_QUERY_PARAM_HMAC,
    QR_SIGNIN_MODAL_QUERY_PARAM_SITEID,
    QR_SIGNIN_MODAL_QUERY_PARAM_KEY,
    QR_SIGNIN_MODAL_QUERY_PARAM_NAME,
} from './constants';
import {type PossibleChildSignInsQuery} from './__generated__/queries.api-types';

const CHILD_SIGNIN_FORM_PREFIX = 'CHILD_SIGNIN';

export const childSignInFormName = (childId: string) => `${CHILD_SIGNIN_FORM_PREFIX}_${childId}`;

export const checkingInOutPersonType = (childId: string, rolesIds: string[]) => {
    return rolesIds.includes(childId) ? 'parent' : 'employee';
};

export type ChildrenFromQuery = PossibleChildSignInsQuery['qrSignin']['possibleSignIns']['children'];
export type EmployeesFromQuery = PossibleChildSignInsQuery['qrSignin']['possibleSignIns']['staff'];

type OpeningHours = ChildrenFromQuery[number]['openingHours'][number];
// Isolate the opening hours open result type based on the properties of the type definition
type OpeningHoursOpenResult = Extract<OpeningHours, {from: string; to: string}>;
// Define the typeguard
const isOpeningHoursOpenResult = (openingHours: OpeningHours): openingHours is OpeningHoursOpenResult =>
    'to' in openingHours && 'from' in openingHours;

export const makeChildSignInPossibilities = (children: ChildrenFromQuery) => {
    const mappedChildren = children.map(child => ({
        checkedIn: child.checkedIn,
        childId: child.childInfo.id,
        image: child.childInfo.profileImage?.url || '',
        institutionId: child.childInfo.siteId,
        name: child.childInfo.name.firstName,
        fullName: child.childInfo.name.fullName,
        openingHours: child.openingHours.map(obj => ({
            day: obj.day,
            open: isOpeningHoursOpenResult(obj),
            fromHour: isOpeningHoursOpenResult(obj) ? parseInt(obj.from.split(':')[0], 10) : 0,
            fromMinute: isOpeningHoursOpenResult(obj) ? parseInt(obj.from.split(':')[1], 10) : 0,
            toHour: isOpeningHoursOpenResult(obj) ? parseInt(obj.to.split(':')[0], 10) : 0,
            toMinute: isOpeningHoursOpenResult(obj) ? parseInt(obj.to.split(':')[1], 10) : 0,
        })),
        relations: child.childInfo.relations.map(relation => ({
            image: relation.profileImage?.url || '',
            name: relation.name.firstName,
            relationId: relation.relationId,
            fullName: relation.name.fullName,
        })),
        requirePickupInfo: child.requirePickupInfo,
        timezone: child.childInfo.zoneId,
        statements: child.statements.map(statement => ({
            statementId: statement.statementId,
            type: statement.statementType,
            title: statement.title,
        })),
        showPickupUntilExactClosingTime: child.showPickupUntilExactClosingTime,
    }));
    const transformedChildren = mappedChildren.reduce(
        (acc, child) => acc.set(child.childId, childCheckinFromAPI(child)),
        Map(),
    );
    return transformedChildren;
};

export const makeStaffSignInPossibilities = (staff: EmployeesFromQuery) => {
    return staff.map(staff => ({
        employeeId: staff.employee.id,
        checkedIn: Boolean(staff.signedIn),
        checkedInGroup: staff.signedIn,
        groups: staff.groups.map(group => ({
            groupId: group.id,
            title: group.title,
            image: group.profileImage?.url || '',
        })),
        image: staff.employee.profileImage?.url || '',
        institution: {
            image: staff.site.profileImage?.url || '',
            institutionId: staff.employee.siteId,
            title: staff.site.title,
        },
        institutionId: staff.employee.siteId,
        name: staff.employee.name.firstName,
        fullName: staff.employee.name.fullName,
        statements: staff.statements.map(statement => ({
            statementId: statement.statementId,
            type: statement.statementType,
            title: statement.title,
        })),
    }));
};

export const makeChildFormData = (childSignIn, pickupRequired, showStatements): GenericFormData => {
    const {fromMinute = 0, fromHour = 0, toHour = 0, toMinute = 0} = childSignIn?.openingHours ?? {};

    const {latestPickupHour, latestPickupMinute} = ChildPickupModalHelpers.getLatestPickupTime({
        toHour,
        toMinute,
        showPickupUntilExactClosingTime: childSignIn.showPickupUntilExactClosingTime ?? false,
    });

    /**
     * `latestAllowedPickupTime` is used as both the maximum allowed value for the time-picker
     * as well as the default value. This was introduced to resolve a bug where the default
     * time/initial value would exceed the max. To discourage this from happening both values
     * are now referenced from this variable.
     */
    const latestAllowedPickupTime = {
        hours: latestPickupHour,
        minutes: latestPickupMinute,
    };

    const statements: Statement[] = childSignIn.statements;
    const statementsSection: GenericFieldElement | null =
        hasValue(statements) && isNonEmpty(statements)
            ? {
                  fieldType: GenericFieldType.element,
                  props: {
                      noLabel: true,
                  },
                  fields: [
                      {
                          fieldType: GenericFieldType.section,
                          name: 'statements',
                          // The section -> element -> checkbox is a terrible hack
                          // because my PM pushed me to add spacing between the checkboxes
                          // which is out of spec
                          fields: statements.map((statement, i) => ({
                              fieldType: GenericFieldType.element,
                              label: i === 0 ? i18next.t('checkin.answerChecks') : null,
                              props: {
                                  noLabel: i > 0,
                              },
                              fields: [
                                  {
                                      fieldType: GenericFieldType.checkbox,
                                      name: statement.statementId,
                                      value: false,
                                      text: statement.title,
                                      validate: [required],
                                      props: {
                                          ['data-e2e-id']: 'sign-in-statements',
                                      },
                                  },
                              ],
                          })),
                      },
                  ],
                  onlyRenderIf: showStatements,
              }
            : null;

    return {
        fields: [
            {
                name: 'pickupTime',
                fieldType: GenericFieldType.newTimepicker,
                label: i18next.t('checkin.pickupAt'),
                value: showStatements ? null : latestAllowedPickupTime,
                validate: pickupRequired ? [required] : undefined,
                props: {
                    min: {
                        hours: fromHour,
                        minutes: fromMinute,
                    },
                    max: latestAllowedPickupTime,
                    placeholder: i18next.t('checkin.expectedPickupAt'),
                    isClearable: !pickupRequired,
                },
                elementProps: {
                    ['data-e2e-id']: 'sign-in-pickup-time',
                },
                onlyRenderIf: !childSignIn?.checkedIn,
            },
            ...(hasValue(statementsSection) ? [statementsSection] : []),
        ],
    };
};

export const submitButtonCSS = css`
    width: 100%;
    border-radius: ${getSpacing(s2)};
    font-size: 16px;
    height: 48px;
    font-weight: 400;
    ${Breakpoints.mobileLandscapeAndLarger`
            width: 100%;
        `}
`;
export const submitButtonContainerCSS = css`
    position: fixed;
    ${Breakpoints.mobileLandscapeAndLarger`
            position: absolute;
        `}
    bottom: 0;
    left: 0;
    width: 100%;
    padding: ${getSpacing(s6)} ${getSpacing(s4)};
    box-sizing: border-box;
    background: ${props => props.theme.mf.colorPalette.n0};

    button[type='submit'],
    .submit {
        ${submitButtonCSS}
    }
`;

export interface QRCodeValues {
    hmac: string | undefined;
    siteId: string | undefined;
    key: string | undefined;
}

export const readFromQRCodeLink = (link: string): QRCodeValues => {
    const params = link.substring(link?.indexOf(QR_SIGNIN_MODAL_QUERY_PARAM_NAME)).split('&');

    const hmac = params?.find(el => el.includes(QR_SIGNIN_MODAL_QUERY_PARAM_HMAC))?.split('=')[1];
    const siteId = params?.find(el => el.includes(QR_SIGNIN_MODAL_QUERY_PARAM_SITEID))?.split('=')[1];
    const key = params?.find(el => el.includes(QR_SIGNIN_MODAL_QUERY_PARAM_KEY))?.split('=')[1];

    return {
        hmac,
        siteId,
        key,
    };
};

export const skipHistoryBack = (state: Pick<RootState, 'modals'>) => state.modals.signIn.isQRScannerCameraOpen;
